/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2023, Zumper
 * @description PinMarker
 */

import { useHover } from '@zcode-ui/hook'
import type { Listable, Pin, RawPinInventorySearch } from '@zcode/state-type'
import React, { useState } from 'react'

import type { MixpanelMaplistClickType, PageName } from '../../../types'
import { PinMarkerDesktop } from './PinMarkerDesktop'
import { PinMarkerMobile } from './PinMarkerMobile'

export interface PinMarkerProps {
  isDot: boolean
  isHovered: boolean
  isMobile: boolean
  isSpotlight?: boolean
  isVisited: boolean
  lat?: number
  listable: {
    pin: Listable | Pin<RawPinInventorySearch>
    showPriceMarker: boolean
  }
  lng?: number
  mixpanelMaplistClick: ({
    buttonClicked,
    entity,
    pageName,
    section,
  }: MixpanelMaplistClickType) => void
  priceTxt: string
  onClick: () => void
  onMouseOut: () => void
  onMouseOver: () => void
}

export const PinMarker = ({
  isDot,
  isHovered,
  isMobile,
  isSpotlight,
  isVisited,
  lat,
  listable,
  lng,
  mixpanelMaplistClick,
  priceTxt,
  onClick,
  onMouseOut,
  onMouseOver,
}: PinMarkerProps) => {
  const [markerRef, isMarkerHovered] = useHover<HTMLDivElement>()
  const [isOpen, setIsOpen] = useState(false)

  const onClose = () => setIsOpen(false)
  const onToggle = () => setIsOpen((prev) => !prev)

  const hovered = isMarkerHovered || isHovered

  const handlePinClick = () => {
    onClick()
    onToggle()
  }

  if (isMobile) {
    return (
      <PinMarkerMobile
        handlePinClick={handlePinClick}
        isActive={isOpen}
        isDot={isDot}
        isSpotlight={isSpotlight}
        isVisited={isVisited}
        lat={lat}
        lng={lng}
        markerRef={markerRef}
        mixpanelMaplistClick={mixpanelMaplistClick}
        onClose={onClose}
        onMouseOut={onMouseOut}
        onMouseOver={onMouseOver}
        previewListable={listable}
        priceTxt={priceTxt}
      />
    )
  }

  return (
    <PinMarkerDesktop
      handlePinClick={handlePinClick}
      hovered={hovered}
      isActive={isOpen}
      isDot={isDot}
      isSpotlight={isSpotlight}
      isVisited={isVisited}
      lat={lat}
      lng={lng}
      mixpanelMaplistClick={mixpanelMaplistClick}
      markerRef={markerRef}
      onClose={onClose}
      onMouseOut={onMouseOut}
      onMouseOver={onMouseOver}
      previewListable={listable}
      priceTxt={priceTxt}
    />
  )
}
